import React, { useMemo, useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import Portal from "@/components/Portal";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import useEscapeToClose from "@/hooks/useEscapeToClose";
import { Stethoscope, BadgeCheck, Sparkles, X, ChevronDown, ChevronUp, Filter, Award, SlidersHorizontal } from "lucide-react";
import { TEAM, DEPARTMENTS, ELIG_FREE, ELIG_RATES } from "@/data/team";

/* ───────────────────────── PAGE ───────────────────────── */
export default function Doctors() {
  const [depts, setDepts] = useState(() => new Set());
  const [elig, setElig] = useState("all");
  const [showAllDepts, setShowAllDepts] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleDept = (name) => {
    setDepts((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const list = useMemo(() => TEAM.filter((d) => {
    if (depts.size > 0 && !depts.has(d.dept)) return false;
    if (elig !== "all" && d.elig !== elig) return false;
    return true;
  }), [depts, elig]);

  const clearAll = () => { setDepts(new Set()); setElig("all"); };
  const filterCount = depts.size + (elig !== "all" ? 1 : 0);
  const VISIBLE = showAllDepts ? DEPARTMENTS.length : 8;

  /* Lock body scroll while mobile filter sheet is open */
  useBodyScrollLock(mobileOpen);
  useEscapeToClose(mobileOpen, () => setMobileOpen(false));

  return (
    <SiteLayout>
      {/* ────────── HERO ────────── */}
      <section className="relative overflow-hidden border-b border-[#E1E8F0]" data-testid="doctors-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FCEDE7] via-[#FAFBFC] to-[#EAF1FA]" />
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
        <div className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full bg-[#C89B3C]/12 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 w-[380px] h-[380px] rounded-full bg-[#A53A2A]/10 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-14 lg:py-20">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.02] tracking-tight animate-[fadeUp_0.6s_ease-out_both]">
              Our <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">Doctors.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#5C6B7A] leading-relaxed animate-[fadeUp_0.7s_ease-out_0.1s_both]">
              {TEAM.length} specialists across {DEPARTMENTS.length} departments — sorted by years of experience. Refine by speciality and Nirmal Privilege Card eligibility.
            </p>
          </div>
        </div>
      </section>

      {/* ────────── FILTERS — compact sticky bar on all viewports (drawer-based) ────────── */}
      <section className="sticky top-[68px] md:top-[116px] z-30 bg-white/95 backdrop-blur-md border-b border-[#E1E8F0] shadow-[0_6px_20px_-15px_rgba(15,37,64,0.2)]" data-testid="doctors-filters">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 py-3 flex items-center gap-3">
          {/* Results count */}
          <div className="text-sm text-[#0F2540] flex-shrink-0">
            <b className="font-display text-base">{list.length}</b>
            <span className="text-[#5C6B7A]"> / {TEAM.length} <span className="hidden sm:inline">doctors</span></span>
          </div>

          {/* Active filter chips — desktop only, horizontally scrollable */}
          <div className="hidden md:flex flex-1 items-center gap-1.5 overflow-x-auto scrollbar-thin py-1">
            {filterCount === 0 ? (
              <span className="text-xs text-[#5C6B7A] italic">No filters applied · sorted by experience</span>
            ) : (
              <>
                {elig !== "all" && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] text-white">
                    {elig === ELIG_FREE ? <Sparkles className="h-3 w-3" /> : <Award className="h-3 w-3" />}
                    {elig === ELIG_FREE ? "Free Consultation" : "Privilege Rates"}
                    <button type="button" onClick={() => setElig("all")} className="ml-0.5 hover:opacity-80" aria-label="Remove filter">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {[...depts].map((name) => (
                  <span key={name} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap bg-[#0F2540] text-white">
                    {name}
                    <button type="button" onClick={() => toggleDept(name)} className="ml-0.5 hover:opacity-80" aria-label={`Remove ${name}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <button type="button" onClick={clearAll} className="ml-1 text-[11px] font-semibold text-[#A53A2A] hover:underline whitespace-nowrap" data-testid="doctors-clear-filters">
                  Clear all
                </button>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="ml-auto md:ml-0 relative inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] shadow-[0_8px_22px_-10px_rgba(165,58,42,0.7)] hover:-translate-y-0.5 active:scale-95 transition-all duration-200 flex-shrink-0"
            data-testid="doctors-mobile-filters-open"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
            {filterCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold bg-white text-[#A53A2A]">
                {filterCount}
              </span>
            )}
          </button>
        </div>
      </section>

      {/* ────────── FILTERS drawer — bottom-sheet on mobile, right-drawer on desktop ────────── */}
      {mobileOpen && (
        <Portal>
          <div className="fixed inset-0 z-[55]" data-testid="doctors-filters-sheet">
          <div className="absolute inset-0 bg-[#0F2540]/55 backdrop-blur-sm animate-[fadeUp_0.25s_ease-out_both]" onClick={() => setMobileOpen(false)} />
          <div className="
            absolute bg-white shadow-[0_-20px_60px_-20px_rgba(15,37,64,0.4)] flex flex-col
            inset-x-0 bottom-0 max-h-[88vh] rounded-t-3xl animate-[slideUp_0.3s_ease-out_both]
            md:inset-x-auto md:bottom-0 md:right-0 md:top-0 md:w-[440px] md:max-h-none md:rounded-none md:rounded-l-3xl md:animate-[slideLeft_0.32s_ease-out_both]
          ">
            {/* Drag handle (mobile only) */}
            <div className="md:hidden pt-3 pb-1 flex justify-center flex-shrink-0">
              <div className="h-1.5 w-12 rounded-full bg-[#E1E8F0]" />
            </div>
            {/* Header */}
            <div className="px-5 pt-2 pb-3 flex items-center justify-between border-b border-[#E1E8F0] flex-shrink-0">
              <div>
                <div className="font-display text-lg font-bold text-[#0F2540]">Filter doctors</div>
                <div className="text-[11px] text-[#5C6B7A] mt-0.5">{list.length} of {TEAM.length} match</div>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="h-9 w-9 grid place-items-center rounded-full bg-[#FAFBFC] border border-[#E1E8F0] text-[#5C6B7A] active:scale-95 transition-transform"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#5C6B7A] mb-2.5 flex items-center gap-1.5">
                  <Filter className="h-3.5 w-3.5" /> NPC Eligibility
                </div>
                <div className="grid gap-2">
                  {[
                    ["all",      "All doctors",         BadgeCheck],
                    [ELIG_FREE,  "Free Consultation",   Sparkles],
                    [ELIG_RATES, "Privilege Rates",     Award],
                  ].map(([val, label, Icon]) => {
                    const active = elig === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setElig(val)}
                        aria-pressed={active}
                        className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 border ${
                          active
                            ? "bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] text-white border-transparent shadow-[0_10px_22px_-12px_rgba(165,58,42,0.7)]"
                            : "bg-white text-[#0F2540] border-[#E1E8F0] active:scale-[0.98]"
                        }`}
                        data-testid={`doctors-filter-elig-sheet-${val}`}
                      >
                        <Icon className="h-4 w-4" /> {label}
                        {active && <span className="ml-auto text-[10px] uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">Selected</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#5C6B7A] mb-2.5 flex items-center gap-2">
                  Departments
                  {depts.size > 0 && <span className="px-2 py-0.5 rounded-full bg-[#EAF1FA] text-[10px] normal-case tracking-normal font-bold text-[#1E4F87]">{depts.size} selected</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {DEPARTMENTS.map((name) => {
                    const active = depts.has(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => toggleDept(name)}
                        aria-pressed={active}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all duration-200 border ${
                          active
                            ? "bg-[#0F2540] text-white border-[#0F2540] shadow-[0_10px_22px_-12px_rgba(15,37,64,0.6)]"
                            : "bg-white text-[#0F2540] border-[#E1E8F0] active:scale-95"
                        }`}
                        data-testid={`doctors-filter-dept-sheet-${name.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-[#C89B3C]" : "bg-[#A53A2A]/40"}`} />
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sticky footer */}
            <div className="border-t border-[#E1E8F0] px-5 py-3 flex items-center gap-3 flex-shrink-0">
              {filterCount > 0 ? (
                <button type="button" onClick={clearAll} className="flex-1 px-4 py-3 rounded-full text-sm font-semibold text-[#A53A2A] border border-[#A53A2A]/30 active:scale-95 transition-transform">
                  Clear all
                </button>
              ) : (
                <div className="flex-1 text-center text-xs text-[#5C6B7A] py-2">No filters applied</div>
              )}
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] shadow-[0_10px_22px_-10px_rgba(165,58,42,0.6)] active:scale-95 transition-transform"
                data-testid="doctors-mobile-filters-apply"
              >
                Show {list.length} doctors
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* ────────── DOCTORS GRID ────────── */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="doctors-grid-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div className="text-sm text-[#5C6B7A]">
            Showing <b className="text-[#0F2540] font-display text-base">{list.length}</b> of {TEAM.length} {list.length === 1 ? "doctor" : "doctors"}
            <span className="ml-2 text-xs text-[#5C6B7A]/80">· sorted by experience</span>
          </div>
          {list.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EAFBEE] text-[#1F7A3D] font-semibold">
                <Sparkles className="h-3 w-3" /> Free Consultation in NPC
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FBF2DC] text-[#7A5715] font-semibold">
                <Award className="h-3 w-3" /> Privilege Rates in NPC
              </span>
            </div>
          )}
        </div>

        {list.length === 0 ? (
          <div className="text-center py-24 animate-[fadeUp_0.5s_ease-out_both]" data-testid="doctors-empty">
            <div className="inline-flex h-16 w-16 rounded-full bg-gradient-to-br from-[#FCEDE7] to-[#FFFFFF] grid place-items-center ring-1 ring-[#E1E8F0]">
              <Stethoscope className="h-7 w-7 text-[#C89B3C]" />
            </div>
            <h3 className="mt-5 font-display text-2xl font-bold text-[#0F2540]">No doctors match these filters</h3>
            <p className="mt-2 text-sm text-[#5C6B7A]">Try removing a department or switching the NPC eligibility option.</p>
            <button type="button" onClick={clearAll} className="mt-6 btn-primary text-sm px-5 py-2.5">Clear all filters</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((d, i) => <DoctorCard key={d.n} d={d} i={i} />)}
          </div>
        )}
      </section>
    </SiteLayout>
  );
}

/* ────────── DOCTOR CARD ────────── */
function DoctorCard({ d, i }) {
  const isFree = d.elig === ELIG_FREE;
  return (
    <article
      className="group relative rounded-3xl overflow-hidden bg-white ring-1 ring-[#E1E8F0] active:scale-[0.99] hover:ring-transparent hover:shadow-[0_36px_60px_-25px_rgba(15,37,64,0.3)] hover:-translate-y-1.5 active:shadow-[0_18px_36px_-18px_rgba(15,37,64,0.25)] transition-all duration-500 animate-[fadeUp_0.55s_ease-out_both] will-change-transform"
      style={{ animationDelay: `${i * 45}ms` }}
      data-testid={`doctor-card-${i}`}
    >
      {/* Animated gradient ring on hover */}
      <div
        className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: "linear-gradient(135deg, rgba(165,58,42,0.6), rgba(200,155,60,0.6), rgba(30,79,135,0.6))",
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: 1.5,
        }}
      />

      {/* Color accent strip (always visible — gives instant visual variety on mobile too) */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${isFree ? "bg-gradient-to-r from-[#1F7A3D] to-[#155F2D]" : "bg-gradient-to-r from-[#E3BC58] to-[#9C7421]"} z-10`} />

      <div className="relative flex">
        {/* Photo */}
        <div className="relative w-[44%] flex-shrink-0 bg-gradient-to-br from-[#EAF1FA] via-[#FAFBFC] to-[#FCEDE7] overflow-hidden">
          <img
            src={d.image}
            alt={d.n}
            loading="lazy"
            className="w-full h-full object-cover min-h-[300px] transition-all duration-700 ease-out group-hover:scale-110 group-hover:rotate-1"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#0F2540]/40 via-transparent to-transparent opacity-65 group-hover:opacity-25 transition-opacity duration-500" />
          {/* Always-on subtle vignette (depth on mobile) */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_140%,rgba(0,0,0,0.18),transparent_55%)]" />
          {/* Shine sweep on hover */}
          <div className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent" />
          {/* Department chip — always visible (good on mobile) */}
          <div className="absolute bottom-3 left-3 right-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.18em] font-bold text-white bg-[#0F2540]/85 backdrop-blur-sm ring-1 ring-white/20 max-w-full">
              <span className="h-1 w-1 rounded-full bg-[#C89B3C] flex-shrink-0" />
              <span className="truncate">{d.dept}</span>
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 p-5 flex flex-col relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#FCEDE7] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold leading-snug bg-gradient-to-r from-[#A53A2A] to-[#7A2A1E] bg-clip-text text-transparent">
              {d.role}
            </div>
            <h3 className="mt-2 font-display text-lg font-bold text-[#0F2540] leading-tight">
              {d.n}
            </h3>
            <div className="mt-2 h-[2px] w-10 bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] origin-left group-hover:scale-x-[1.6] transition-transform duration-500 rounded-full" />
            {d.qualification && (
              <p className="mt-3 text-xs text-[#5C6B7A] leading-relaxed break-words">{d.qualification}</p>
            )}

            {d.experience && (
              <div className="mt-3 flex items-center gap-2.5">
                <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-[#EAF1FA] to-[#FFFFFF] ring-1 ring-[#1E4F87]/15 grid place-items-center text-[#1E4F87] flex-shrink-0 group-hover:rotate-6 transition-transform duration-500">
                  <Award className="h-3.5 w-3.5" />
                </div>
                <div className="leading-tight">
                  <div className="font-display text-base font-bold text-[#0F2540]">{d.experience}</div>
                  <div className="text-[9px] uppercase tracking-wider text-[#5C6B7A] font-semibold">Experience</div>
                </div>
              </div>
            )}
          </div>

          <div className="relative mt-auto pt-4">
            {isFree ? (
              <div className="group/badge inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white bg-gradient-to-r from-[#1F7A3D] to-[#155F2D] shadow-[0_8px_18px_-8px_rgba(31,122,61,0.6)] hover:shadow-[0_12px_24px_-8px_rgba(31,122,61,0.7)] transition-all duration-300" data-testid={`doctor-badge-free-${i}`}>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                <Sparkles className="h-3 w-3 group-hover/badge:rotate-12 transition-transform" /> Free Consultation 
              </div>
            ) : (
              <div className="group/badge inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-[#7A5715] bg-gradient-to-r from-[#F5E2B1] via-[#E3BC58] to-[#C89B3C] shadow-[0_8px_18px_-8px_rgba(200,155,60,0.6)] hover:shadow-[0_12px_24px_-8px_rgba(200,155,60,0.7)] transition-all duration-300" data-testid={`doctor-badge-rates-${i}`}>
                <Award className="h-3 w-3 group-hover/badge:rotate-12 transition-transform" /> Privilege Rates
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
